import { Chip, SxProps, Tooltip } from "@mui/material";
import { MouseEvent, ReactNode } from "react";

type Props = {
  label: string | ReactNode;
  onDelete: (event: any) => void;
  disabled?: boolean;
  readOnly?: boolean;
  labelSx?: SxProps;
  variant?: "filled" | "outlined";
  alwaysStopPropagation?: boolean;
  tooltip?: string;
};

const AutocompleteChip = ({
  label,
  onDelete,
  disabled = false,
  readOnly,
  labelSx,
  variant = "filled",
  alwaysStopPropagation = false,
  tooltip,
  ...props
}: Props) => {
  const handleMouseDown = (event: MouseEvent) => {
    if (alwaysStopPropagation) {
      // Always stop propagation (for Select context)
      event.stopPropagation();
    } else {
      // For Autocomplete components
      // Only stop propagation if not clicking on delete icon
      // This allows onDelete to work properly when limitTags is used
      // https://github.com/mui/material-ui/issues/42432 -> fixed in MUI v6.xx
      const target = event.target as HTMLElement;
      const isDeleteIcon = target.closest(".MuiChip-deleteIcon");
      if (!isDeleteIcon) {
        event.stopPropagation();
      }
    }
  };

  const chip = (
    <Chip
      onMouseDown={handleMouseDown}
      sx={{
        "& .MuiChip-label": {
          fontWeight: 600,
          fontSize: "14px",
          ...(labelSx as {}),
        },
        height: "24px",
        // If chip state is disabled, it cannot be removed from the list of chips
        // so we want to make the chip look like disabled, but can be still removed
        ...(disabled && { opacity: 0.4 }),
      }}
      // If readOnly is true, the chip cannot be removed from the list of chips
      disabled={readOnly}
      color="primary"
      variant={variant}
      label={label}
      onDelete={onDelete}
      {...props}
    />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{chip}</Tooltip>;
  }

  return chip;
};

export default AutocompleteChip;
